import React, { useContext, useEffect, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { faSlidersH } from '@fortawesome/pro-light-svg-icons';
import { Button, NoItemsCard, StickyContext } from '@ygtechnologies/rubus-ui';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';

import { useDefaultScrollToId, useCanScroll, useIsMobile } from '@/utils/hooks';
import { classnames } from '@/utils/html';
import { ConfiguratorGroupDto, ConfiguratorRoomDto } from '@/services/configurator';
import { useOptionConfigurator } from '@/components/OptionConfigurator/OptionConfiguratorContext';

import theme from './OptionsPage.module.scss';
import { ShowroomOnly } from '@/components/OptionConfigurator/ShowroomVisibility';
import { ShowroomTotalCard } from './ShowroomTotalCard';
import { useOptionsScope } from './OptionsScope';
import { useOptionsData } from './useOptionsData';
import { useOptionsFilter } from './useOptionsFilter';
import { getGroupStatus } from './shared/groupStatus';
import { useFilteredGroups } from './shared/useFilteredGroups';
import { FilterSidebar } from './filter/FilterSidebar';
import { DeadlineBlock } from './deadlineBlock/DeadlineBlock';
import { PresetCard } from './presets/PresetCard';

/** A v2 group paired with the room it came from (same groupId can appear in multiple rooms). */
export interface RoomGroup {
    room: ConfiguratorRoomDto;
    group: ConfiguratorGroupDto;
}

export type SelectionChangedEventHandler = () => void;

export const OptionsPage: React.FC = () => {
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

    const { filter, setStatuses } = useOptionsFilter();
    const { projectData } = useOptionsScope();

    // Stick below every sticky ancestor; summing handles both seller (header+tabs) and buyer (header).
    // The showroom has no StickyContainer ancestor (offset would be 0), so pin to the container's
    // own 16px inset ($card-spacing) to keep the sidebar aligned with the content.
    const { currentOffset, stickyHeight } = useContext(StickyContext);
    const { isShowroom } = useOptionConfigurator();
    const stickyTop = isShowroom ? 16 : currentOffset + stickyHeight;
    const isMobile = useIsMobile();

    // Cap the filter to the viewport below the offset so it scrolls internally and stays pinned.
    // Skipped on mobile (Drawer overlay).
    const sidebarStyle = isMobile
        ? undefined
        : { top: stickyTop, maxHeight: `calc(100vh - ${stickyTop}px - 16px)` };

    const { rooms, deadlines, presetGroups, filterFacets, isLoading, refetch } = useOptionsData();

    const filteredGroups = useFilteredGroups(rooms, deadlines, filter, isShowroom);

    const defaultExpandedId = useDefaultScrollToId();
    const canScroll = useCanScroll(defaultExpandedId);

    useEffect(() => {
        if (!filteredGroups || !defaultExpandedId) return;
        const hasSignedGroupForDeadline = filteredGroups.some(
            ({ group }) => group.deadlineId === defaultExpandedId && getGroupStatus(group, isShowroom) === 'Sent'
        );
        if (hasSignedGroupForDeadline) {
            setStatuses(['Default', 'Reviewed', 'Selected', 'Sent']);
        }
    }, [defaultExpandedId, setStatuses, filteredGroups, isShowroom]);

    const handleSelectionChange = () => {
        void refetch();
    };

    const buyerHasForcedPreset = presetGroups.some(
        (pg) => pg.group.forceSelection && !pg.group.styles.some((s) => s.isPicked)
    );

    if (!projectData) {
        return null;
    }

    return (
        <div className={theme.container}>
            <Button
                variant="tertiary"
                onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
                className={theme.mobileFilterButton}
                icon={faSlidersH}>
                <Trans>Filters</Trans>
            </Button>

            <div className={classnames(theme.content, { [theme.contentShowroom]: isShowroom })}>
                <aside className={theme.sidebar} style={sidebarStyle}>
                    <FilterSidebar
                        isMobileOpen={isMobileFilterOpen}
                        onMobileClose={() => setIsMobileFilterOpen(false)}
                        facets={filterFacets}
                    />
                </aside>

                {!filteredGroups || isLoading ? (
                    <div className={theme.main}>
                        <LoadingIndicator />
                    </div>
                ) : (
                    <div className={theme.main}>
                        {presetGroups.map((pg) => (
                            <PresetCard
                                key={pg.group.id}
                                presetGroup={pg}
                                onSelectionChanged={handleSelectionChange}
                            />
                        ))}
                        <div className={buyerHasForcedPreset ? theme.disabled : undefined} aria-disabled={buyerHasForcedPreset}>
                            {deadlines.map((dl) => {
                                const groupsForDeadline = filteredGroups.filter(({ group }) => group.deadlineId === dl.id);
                                const unfilteredForDeadline =
                                    rooms?.flatMap((r) =>
                                        r.groups.filter((g) => g.deadlineId === dl.id).map((g) => ({ room: r, group: g }))
                                    ) ?? [];
                                return (
                                    <DeadlineBlock
                                        key={dl.id}
                                        deadline={dl}
                                        shouldScrollTo={canScroll && defaultExpandedId === dl.id}
                                        groupsForDeadline={groupsForDeadline}
                                        unfilteredForDeadline={unfilteredForDeadline}
                                        onSelectionChanged={handleSelectionChange}
                                    />
                                );
                            })}
                            {rooms !== null && rooms.flatMap((r) => r.groups).length === 0 && (
                                <NoItemsCard
                                    title={
                                        <Trans>There are no options available for you at this point in time.</Trans>
                                    }
                                />
                            )}
                            {filteredGroups.length === 0 && rooms !== null && rooms.flatMap((r) => r.groups).length > 0 && (
                                <NoItemsCard
                                    title={
                                        <Trans>
                                            There are no options to display because there is no options for this
                                            deadline with the selected filters. Please change deadline or filters.
                                        </Trans>
                                    }
                                />
                            )}
                        </div>
                    </div>
                )}

                <ShowroomOnly>
                    <aside className={theme.totalColumn} style={sidebarStyle}>
                        <ShowroomTotalCard />
                    </aside>
                </ShowroomOnly>
            </div>
        </div>
    );
};

